/* eslint-disable */
import { FormInstance } from '@duxton-web/ui';
import _ from 'lodash';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid4 } from 'uuid';

import {
  CONTENT_GENERATION_CONFIG_GET_LATEST,
  CP_LIST_PRODUCTS,
  contentGenerationConfigGetLatest,
  generateContentViaMystique,
  generateContentViaMystiqueFeedback,
} from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import { DEFAULT_LOCALE } from 'omega-rtc/config';
import {
  MAX_CONTENT_FOR_MANUAL_MODE,
  PN_IMAGE_KEY,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_PRIMARY_LANGUAGE,
  SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_SLANG,
  COUNTRY_ID_VS_COUNTRY_CODE,
} from 'omega-rtc/constants/ucd';
import {
  buildUcdContentGenerationRequestForMystiqueCall,
  fillContentContainerFormValues,
  generateReqPayloadForMystiqueCall,
  isContentPortalProductsEnabled as getIsContentPortalProductsEnabled,
  getSelectedContentPortalProduct,
  getSlangName,
  insertMystiqueMetaDataToFormItems,
  isContentPortalCVPOthersSelected,
  isCVPOthersSelected,
  validateSmartCampaignFields,
} from 'omega-rtc/utils/smart-campaign-utils';
import {
  getRequestStateKey,
  getSmartCommsRoundNameFromNumber,
  getSmartCommsVariantNameFromNumber,
} from 'omega-rtc/utils/ucd-utils';
import { useContentGenerationEndpointMigrationEnabled } from 'omega-rtc/utils/portal-config';
import {
  productDisplayName,
  productToApiBody,
} from 'omega-rtc/views/ucd/content-config/adapters';

interface CallToActionOption {
  label: string;
  value: string;
}

type CallToActionOptions = CallToActionOption[];

interface VariantInPrimaryLanguageContents {
  title: string;
  message: string;
  isChosen: boolean;
  uuid: string;
  slang?: string;
  isUpdated?: boolean;
  hedwigTemplateID?: string;
}

interface PrimaryLanguageContents {
  lastVariantNumber: number;
  lastRoundNumber: number;
  selectedControlGroupVariant?: string; // control group variantName
  rounds: Record<string, Record<string, VariantInPrimaryLanguageContents>>; //  eg: rounds: { ['round_name']:{[variant_name]: {title: 'sample title', message: 'sample message', isChosen: true, uuid: 'sample_uuid' }} }
  mode: string; // eg: automatic or manuel content generation
}

const primaryLanguageContentsInitialValues: PrimaryLanguageContents = {
  lastVariantNumber: 0,
  lastRoundNumber: 0,
  selectedControlGroupVariant: undefined,
  rounds: {},
  mode: SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic,
};
const defaultPrimaryLanguage = DEFAULT_LOCALE;
const EMPTY_ARRAY: any[] = [];

interface MystiqueRequestMetaData {
  contents: string[];
  otherMetaData?: {
    primaryLanguage: string;
    isUserEditModeActiveForMystiqueInputs: boolean;
  };
  requestID?: string;
  isSlangModeEnabled: boolean;
  isRequestPending: boolean;
  isReGenerateMode: boolean;
  previousLanguage?: string;
}

interface GenerateMystiqueContentsProps {
  primaryLanguage: string;
  isUserEditModeActiveForMystiqueInputs?: boolean;
  forceGenerate?: boolean;
  previousRequestID?: string;
}

interface ClearContentVariantsProps {
  shouldClearSlangToggle?: boolean;
}

const defaultMystiqueMetaData = {
  contents: [],
  otherMetaData: undefined, // { primaryLanguage: "", isUserEditModeActiveForMystiqueInputs: "" }
  requestID: undefined,
  isSlangModeEnabled: false,
  isRequestPending: false,
  isReGenerateMode: false,
  previousLanguage: undefined,
};

const formatContentGenerationDate = (value: any) => (value && value.format ? value.format('YYYY-MM-DD') : '');

const CreateSmartCampaignContext = createContext({
  selectedLanguages: [],
  callToActionOptions: [],
  isCampaignLoading: false,
  mystiqueRequestMetaData: {},
  isMystiqueCallPending: false,
  isSaveCampaignLoading: false,
  imageDataForEachLanguage: {},
  isContentVariantsTouched: false,
  showEditableFormInputs: false,

  isCurrentContentSourceManual: false,
  isMystiqueContentEmpty: false,
  isPrimaryLanguageContentsEmpty: false,
  primaryLanguage: defaultPrimaryLanguage,
  numberOfSelectedVariants: 0,

  setIsCampaignLoading: (value: boolean) => {},
  setSelectedLanguages: (value: string[]) => {},
  setMystiqueRequestMetaData: (value: MystiqueRequestMetaData) => {},
  setIsContentVariantsTouched: (value: boolean) => {},
  setIsSaveCampaignLoading: (value: boolean) => {},
  setCallToActionOptions: (value: CallToActionOption[]) => {},
  setImageDataForEachLanguage: (value: Record<string, string>) => {},
  setPrimaryLanguageContentsInForm: (value: PrimaryLanguageContents) => {},
  setShowEditableFormInputs: (value: boolean) => {},

  clearMystiqueRequestMetaData: () => {},
  generateMystiqueContents: ({}: any) => {},
  toggleControlledGroup: (variantName: string) => {},
  primaryLanguageContentsInForm: primaryLanguageContentsInitialValues,
  toggleContentSelection: (variantName: string, roundName: string) => {},
  handleLanguageSelect: (languages: string[], allowReGeneration = true) => {},
  clearContentVariants: ({}: { shouldClearSlangToggle?: boolean }) => {},
  updateImageData: ({
    imageData,
    language,
  }: {
    imageData: any;
    language: string;
  }) => {},
  generateManualModeContents: () => {},
});

const CreateSmartCampaignProvider = ({
  children,
  form,
}: React.PropsWithChildren & { form: FormInstance<any> }) => {
  /* --- States ---- */
  // first element in the selectedLanguages will be primaryLanguage.
  const [selectedLanguages, setSelectedLanguages] = useState<Array<string>>([
    defaultPrimaryLanguage,
  ]);
  // type is : {[languageCode: string]: UploadFile(of duxton) } eg: {'en': {uid: '', url: '', name: '',status: '' } }
  const [imageDataForEachLanguage, setImageDataForEachLanguage] =
    useState<Object>({ PN_IMAGE_KEY: '' }); // TODO: Need to replace the  PN_IMAGE_KEY in future for multi language support
  const [callToActionOptions, setCallToActionOptions] =
    useState<CallToActionOptions>([]);
  const [isContentVariantsTouched, setIsContentVariantsTouched] =
    useState(false);
  const [primaryLanguageContentsInForm, setPrimaryLanguageContentsInForm] =
    useState<PrimaryLanguageContents>(primaryLanguageContentsInitialValues);
  const [isCampaignLoading, setIsCampaignLoading] = useState(false);
  const [isSaveCampaignLoading, setIsSaveCampaignLoading] = useState(false);
  const [showEditableFormInputs, setShowEditableFormInputs] = useState(false);
  const [mystiqueRequestMetaData, setMystiqueRequestMetaData] =
    useState<MystiqueRequestMetaData>(defaultMystiqueMetaData);

  const dispatch = useDispatch();

  /* --- UseSelectors --- */
  const projectView = useSelector((state: any) => state.ucd.projectView);
  const countryID =
    projectView && projectView.project && projectView.project.countryID;
  const countryCode =
    (countryID && COUNTRY_ID_VS_COUNTRY_CODE[countryID]) || '';
  const contentPortalProducts = useSelector(
    (state: any) => _.get(state, `ucd.cpProductsByCountry.${countryCode}.active`, EMPTY_ARRAY),
  );
  const hasContentPortalProductsLoaded = useSelector(
    (state: any) => Boolean(
      countryCode &&
      _.has(state, ['ucd', 'cpProductsByCountry', countryCode, 'active']),
    ),
  );
  const hasContentPortalProductsLoadFailed = useSelector(
    (state: any) => Boolean(
      _.get(state, ['requests', getRequestStateKey(CP_LIST_PRODUCTS), 'isFailure']),
    ),
  );
  const shouldUseContentPortalProducts = getIsContentPortalProductsEnabled();
  const shouldUseUcdContentGeneration = useContentGenerationEndpointMigrationEnabled();
  const contentGenerationConfigLatest = useSelector((state: any) =>
    _.get(state, 'ucd.contentGenerationConfigLatest', {}));
  const contentGenerationConfigLatestRequest = useSelector((state: any) =>
    _.get(state, ['requests', getRequestStateKey(CONTENT_GENERATION_CONFIG_GET_LATEST)], {}));
  const baseConfigVersion = Number(_.get(contentGenerationConfigLatest, 'version', 0)) || 0;

  useEffect(() => {
    if (!shouldUseUcdContentGeneration) {
      return;
    }
    dispatch(contentGenerationConfigGetLatest());
  }, [dispatch, shouldUseUcdContentGeneration]);

  /* --- UseMemo --- */
  const primaryLanguage = useMemo(
    // First element in selectedLanguages will be primary language.
    () => selectedLanguages[0],
    [selectedLanguages],
  );

  const isMystiqueCallPending = useMemo(
    () => mystiqueRequestMetaData.isRequestPending,
    [mystiqueRequestMetaData],
  );

  const isCurrentContentSourceManual = useMemo(() => {
    return (
      primaryLanguageContentsInForm.mode ===
      SMART_CAMPAIGN_CONTENT_SOURCE_MODES.manual
    );
  }, [primaryLanguageContentsInForm]);

  const isPrimaryLanguageContentsEmpty = useMemo(
    () => !primaryLanguageContentsInForm.lastRoundNumber,
    [primaryLanguageContentsInForm],
  );

  const isMystiqueContentEmpty = useMemo(() => {
    if (isCurrentContentSourceManual) {
      return true;
    }
    return isPrimaryLanguageContentsEmpty;
  }, [isPrimaryLanguageContentsEmpty, isCurrentContentSourceManual]);

  const numberOfSelectedVariants = useMemo(() => {
    // Use reduce to accumulate the count of chosen variants across all rounds
    const selectedVariantsCounts =
      (primaryLanguageContentsInForm &&
        Object.keys(primaryLanguageContentsInForm.rounds).reduce(
          (total, roundKey) => {
            // For each round, filter the variants where isChosen is true, then get the length of this filtered array
            const chosenCount = Object.keys(
              primaryLanguageContentsInForm.rounds[roundKey],
            ).filter(
              (variantKey) =>
                primaryLanguageContentsInForm.rounds[roundKey][variantKey]
                  .isChosen,
            ).length;
            return total + chosenCount;
          },
          0,
        )) ||
      0;
    return (
      selectedVariantsCounts +
      (primaryLanguageContentsInForm.selectedControlGroupVariant ? 1 : 0)
    );
  }, [primaryLanguageContentsInForm]);

  /* --- Util functions ---*/
  const clearContentVariants = ({
    shouldClearSlangToggle = true,
  }: ClearContentVariantsProps) => {
    setPrimaryLanguageContentsInForm((prev) => ({
      ...prev,
      rounds: {},
      lastRoundNumber: 0,
      lastVariantNumber: 0,
      selectedControlGroupVariant: undefined,
    }));

    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT, {});
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.FEEDBACK, '');

    if (shouldClearSlangToggle) {
      form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.SLANG_TOGGLE, false);
    }
  };

  const resetAndReGenerateContents = async (
    newPrimaryLanguage: string,
    allowReGeneration: boolean,
  ) => {
    if (!primaryLanguageContentsInForm.lastRoundNumber) {
      return;
    }
    clearContentVariants({ shouldClearSlangToggle: false });

    if (!allowReGeneration) {
      return;
    }

    if (isCurrentContentSourceManual) {
      generateManualModeContents();
      return;
    }

    // Get the current vertical and CVP values from the form
    const vertical = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL);
    const cvpValue = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP);

    // Use the utility function to determine if CVP is "others"
    const isCVPOthers = shouldUseContentPortalProducts
      ? isContentPortalCVPOthersSelected(cvpValue)
      : isCVPOthersSelected(vertical, cvpValue);

    const validationResult = await validateSmartCampaignFields({
      form,
      isMystiqueContentEmpty,
      isCVPOthers,
      isEditableInputs: showEditableFormInputs,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    });

    if (validationResult.length) {
      const errorMessage = validationResult[0].errors[0];
      snackBar.error({ content: errorMessage });
      return;
    }

    generateMystiqueContents({
      primaryLanguage: newPrimaryLanguage,
      isUserEditModeActiveForMystiqueInputs: false,
      forceGenerate: true,
    });
  };

  const handleLanguageSelect = (
    languages: string[],
    allowReGeneration = true,
  ) => {
    // Checking whether primary language is changed. (Need to clear all contents if it is changed).
    const primaryLanguage = languages[0];
    if (
      primaryLanguage !== selectedLanguages[0] &&
      primaryLanguageContentsInForm.lastRoundNumber > 0
    ) {
      resetAndReGenerateContents(primaryLanguage, allowReGeneration);
      insertMystiqueMetaDataToFormItems({
        projectView,
        formInstance: form,
        language: primaryLanguage,
        setCallToActionOptions,
        contentPortalProducts,
        shouldUseContentPortalProducts,
      });
    }

    setSelectedLanguages(languages);
  };

  const toggleContentSelection = (variantName, roundName) => {
    if (!primaryLanguageContentsInForm.rounds[roundName][variantName]) {
      return;
    }

    if (!isContentVariantsTouched) {
      setIsContentVariantsTouched(true);
    }
    setPrimaryLanguageContentsInForm((previousResponse) => {
      const isChosen = previousResponse.rounds[roundName][variantName].isChosen;
      previousResponse.rounds[roundName][variantName].isChosen = !isChosen;

      return { ...previousResponse };
    });
  };

  const toggleControlledGroup = (variantName) => {
    if (!isContentVariantsTouched) {
      setIsContentVariantsTouched(true);
    }

    setPrimaryLanguageContentsInForm((previousResponse) => {
      const isGivenVariantIsAlreadyInControlGroup =
        previousResponse.selectedControlGroupVariant === variantName;

      previousResponse.selectedControlGroupVariant =
        isGivenVariantIsAlreadyInControlGroup ? undefined : variantName;

      return { ...previousResponse };
    });
  };

  const updateImageData = ({ imageData, language }) => {
    const channel = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);
    //  channel !== SMART_CAMPAIGN_CHANNELS.push - push channel doesn't support multi-language image upload
    if (!language && channel !== SMART_CAMPAIGN_CHANNELS.push) {
      return;
    }

    if (!isContentVariantsTouched) {
      setIsContentVariantsTouched(true);
    }

    setImageDataForEachLanguage((prev) => ({
      ...prev,
      [language || PN_IMAGE_KEY]: imageData,
    }));
  };

  /**
   * Function to generate manual content
   */
  const generateManualModeContents = () => {
    const roundName = getSmartCommsRoundNameFromNumber(1); // Have only one round in manual mode.
    const content = { title: '', message: '' };
    const roundData = {};
    for (let i = 1; i <= MAX_CONTENT_FOR_MANUAL_MODE; i++) {
      const variantName = getSmartCommsVariantNameFromNumber(i);
      roundData[variantName] = {
        ...content,
        isChosen: false,
        uuid: uuid4(),
      };
      fillContentContainerFormValues({
        content,
        roundName: roundName,
        language: primaryLanguage,
        variantName,
        formInstance: form,
      });
    }

    setPrimaryLanguageContentsInForm({
      mode: SMART_CAMPAIGN_CONTENT_SOURCE_MODES.manual,
      lastRoundNumber: 1,
      lastVariantNumber: MAX_CONTENT_FOR_MANUAL_MODE,
      rounds: { [roundName]: roundData },
      selectedControlGroupVariant: undefined,
    });
  };

  const generateMystiqueContents = ({
    primaryLanguage,
    isUserEditModeActiveForMystiqueInputs = false,
    previousRequestID,
    forceGenerate,
  }: GenerateMystiqueContentsProps) => {
    /**
     * mystiqueRequestMetaData.isRequestPending is true, that mean, mystique content is already generating.
     * So, we are only allowing re-request(for slang generation), We need to prevent new requests.
     */
    if (
      mystiqueRequestMetaData.isRequestPending &&
      mystiqueRequestMetaData.requestID !== previousRequestID
    ) {
      return;
    }

    const isSlangModeEnabledInForm = form.getFieldValue(
      SMART_CAMPAIGN_FORM_KEYS.SLANG_TOGGLE,
    );
    const slangName = getSlangName({
      countryID,
      language: primaryLanguage,
    });
    /**
     * We will have two mystique calls if slangMode is ON
     * 1. First call with primary language
     * 2. Second call with slang based language
     * mystiqueRequestMetaData.previousLanguage is used to store language used in current request.
     */
    const allowSlangGenerationInRequest =
      (isSlangModeEnabledInForm ||
        mystiqueRequestMetaData.isSlangModeEnabled) &&
      slangName;

    const generateContentsBasedOnSlang =
      allowSlangGenerationInRequest &&
      mystiqueRequestMetaData.previousLanguage === primaryLanguage; // 1st we will generate contents in primary language, then using slang

    const isInReGenerateMode =
      !forceGenerate &&
      !isMystiqueContentEmpty &&
      !_.isEmpty(form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT));

    const mystiquePayload = generateReqPayloadForMystiqueCall({
      projectView,
      formInstance: form,
      lastRoundNumber: isInReGenerateMode
        ? primaryLanguageContentsInForm.lastRoundNumber
        : 0,
      isEditableInputs: isUserEditModeActiveForMystiqueInputs,
      language: primaryLanguage,
      slangName: generateContentsBasedOnSlang ? slangName : undefined, // We only pass slangName after 1st request
      isSlangEnabled: generateContentsBasedOnSlang,
      contentPortalProducts,
      shouldUseContentPortalProducts,
    });

    if (!mystiquePayload) {
      snackBar.error({ content: 'Oops! Something went wrong' });
      return;
    }

    let ucdContentGenerationProduct;
    let ucdContentGenerationVertical = '';
    if (shouldUseUcdContentGeneration) {
      const vertical = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL);
      const cvp = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP);
      let contentGenerationError = '';

      if (!shouldUseContentPortalProducts) {
        contentGenerationError = 'Content generation through UCD requires Content Portal product offerings.';
      }

      const selectedProduct = shouldUseContentPortalProducts && !contentGenerationError
        ? getSelectedContentPortalProduct({
          products: contentPortalProducts,
          selectedValue: cvp,
          vertical,
        })
        : undefined;

      if (!contentGenerationError && (!selectedProduct || !selectedProduct.key)) {
        if (hasContentPortalProductsLoadFailed) {
          contentGenerationError = 'Unable to load Product Offering/CVP options';
        } else if (!hasContentPortalProductsLoaded) {
          contentGenerationError = 'Please wait for Product Offering/CVP options to load';
        } else {
          contentGenerationError = 'Please select a valid Product Offering/CVP';
        }
      }

      if (!contentGenerationError && baseConfigVersion <= 0) {
        contentGenerationError = contentGenerationConfigLatestRequest.isPending
          ? 'Please wait for the content generation config to load.'
          : 'Content generation config is unavailable. Please refresh and try again.';
      }

      if (contentGenerationError) {
        snackBar.error({ content: contentGenerationError });
        return;
      }

      ucdContentGenerationProduct = selectedProduct;
      ucdContentGenerationVertical = vertical;
    }

    const requestCount = allowSlangGenerationInRequest
      ? (
        generateContentsBasedOnSlang
        ? SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_SLANG
        : SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_PRIMARY_LANGUAGE
      )
      : undefined;
    if (requestCount) {
      mystiquePayload.num = requestCount;
    }

    if (ucdContentGenerationProduct && ucdContentGenerationProduct.key) {
      const ucdContentGenerationPayload = buildUcdContentGenerationRequestForMystiqueCall({
        baseConfigVersion,
        countryCode,
        endDate: formatContentGenerationDate(form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.END_DATE)),
        mystiquePayload,
        productName: productDisplayName(ucdContentGenerationProduct, primaryLanguage),
        productOverride: productToApiBody(ucdContentGenerationProduct),
        productUUID: ucdContentGenerationProduct.key,
        startDate: formatContentGenerationDate(form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.START_DATE)),
        vertical: ucdContentGenerationVertical,
      });

      if (!ucdContentGenerationPayload) {
        snackBar.error({ content: 'Content generation config is unavailable. Please refresh and try again.' });
        return;
      }
      mystiquePayload.ucdPayload = ucdContentGenerationPayload;
    }

    if (allowSlangGenerationInRequest) {
      setMystiqueRequestMetaData((prev) => ({
        ...prev,
        contents: [...prev.contents],
        isReGenerateMode: isInReGenerateMode,
        isRequestPending: true,
        isSlangModeEnabled: true,
        otherMetaData: {
          primaryLanguage,
          requestCount,
          isUserEditModeActiveForMystiqueInputs,
        },
        previousLanguage: generateContentsBasedOnSlang
          ? slangName
          : primaryLanguage,
        requestID: uuid4(),
      }));
    } else {
      setMystiqueRequestMetaData({
        ...defaultMystiqueMetaData,
        isRequestPending: true,
      });
    }

    if (isInReGenerateMode) {
      dispatch(generateContentViaMystiqueFeedback(mystiquePayload));
      return;
    }
    dispatch(generateContentViaMystique(mystiquePayload));
  };

  const clearMystiqueRequestMetaData = () => {
    setMystiqueRequestMetaData({ ...defaultMystiqueMetaData });
  };

  return (
    <CreateSmartCampaignContext.Provider
      value={{
        mystiqueRequestMetaData,
        selectedLanguages,
        isCampaignLoading,
        callToActionOptions,
        isMystiqueCallPending,
        isSaveCampaignLoading,
        imageDataForEachLanguage,
        isContentVariantsTouched,
        primaryLanguageContentsInForm,
        showEditableFormInputs,

        primaryLanguage,
        isCurrentContentSourceManual,
        isMystiqueContentEmpty,
        isPrimaryLanguageContentsEmpty,
        numberOfSelectedVariants,

        setMystiqueRequestMetaData,
        setSelectedLanguages,
        setIsCampaignLoading,
        setCallToActionOptions,
        setIsSaveCampaignLoading,
        setIsContentVariantsTouched,
        setImageDataForEachLanguage,
        setPrimaryLanguageContentsInForm,
        setShowEditableFormInputs,

        generateMystiqueContents,
        clearMystiqueRequestMetaData,
        updateImageData,
        handleLanguageSelect,
        clearContentVariants,
        toggleControlledGroup,
        toggleContentSelection,
        generateManualModeContents,
      }}
    >
      {children}
    </CreateSmartCampaignContext.Provider>
  );
};

export const useCreateSmartCampaign = () =>
  useContext(CreateSmartCampaignContext);

export default CreateSmartCampaignProvider;
